import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Radio, ArrowLeft, LogIn } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1557264322-b44d383a2906?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "overlay",
        }}
      />
      <Link
        to="/"
        data-testid="back-to-player"
        className="absolute top-6 left-6 text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--neon-cyan)] flex items-center gap-1.5"
      >
        <ArrowLeft size={14} /> Back to Radio
      </Link>

      <div className="player-card w-full max-w-[400px] p-8 relative">
        <div className="flex items-center gap-2 mb-1">
          <Radio size={18} className="text-[var(--neon-cyan)]" />
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--neon-cyan)]">
            DJ Console
          </span>
        </div>
        <h1 className="font-display text-3xl font-bold mb-2">Sign in</h1>
        <p className="text-[var(--text-muted)] text-sm mb-7">
          Only DJ JAVI G can manage the broadcast queue.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] block mb-1.5">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-dark"
              placeholder="javig@mixxflow.com"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] block mb-1.5">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-dark"
              placeholder="••••••••"
              data-testid="login-password-input"
            />
          </div>

          {err && (
            <p className="text-[#ff5566] text-sm font-mono" data-testid="login-error">{err}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-cyan w-full flex items-center justify-center gap-2 py-3"
            data-testid="login-submit-button"
          >
            <LogIn size={16} /> {loading ? "Signing in..." : "Enter Console"}
          </button>
        </form>

        <div className="border-t border-[var(--border-soft)] mt-7 pt-5 flex items-center justify-center gap-2">
          <span className="brand-powered text-xs">powered by</span>
          <span className="brand-javig text-lg">JAVI G</span>
        </div>
      </div>
    </div>
  );
}
